import React, { useState, useEffect } from 'react';
import toast, { Toaster } from 'react-hot-toast';

const UserDetailsDrawer = ({ isOpen, onClose, user, onUserUpdated }) => {
  const [updatingPlan, setUpdatingPlan] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(user?.plan || '');
  const [statusChanging, setStatusChanging] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);

  useEffect(() => {
    if (user?._id && isOpen) {
      fetch(`http://localhost:5003/admin/users/${user._id}/logs`)
        .then((res) => res.json())
        .then((data) => setActivityLogs(data))
        .catch((err) => console.error('Failed to fetch logs:', err));
    }
  }, [user, isOpen]);




  if (!isOpen || !user) return null;

  const updatePlan = async () => {
    if (selectedPlan === user.plan) return;

    const confirmed = window.confirm(`Change plan from '${user.plan}' to '${selectedPlan}'?`);
    if (!confirmed) return;

    setUpdatingPlan(true);
    try {
      const response = await fetch(`http://localhost:5003/admin/users/${user._id}/plan`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: selectedPlan }),
      });

      const data = await response.json();
      if (response.ok) {
        toast.success('Plan updated successfully');
        onUserUpdated?.();
      } else {
        toast.error(data.message || 'Failed to update plan');
      }
    } catch (err) {
      console.error('Error updating plan:', err);
      toast.error('Server error while updating plan');
    } finally {
      setUpdatingPlan(false);
    }
  };

  const changeUserStatus = async (newStatus) => {
    const confirmed = window.confirm(`Are you sure you want to set user status to '${newStatus}'?`);
    if (!confirmed) return;

    setStatusChanging(true);
    try {
      const response = await fetch(`http://localhost:5003/admin/users/${user._id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(`User ${newStatus === 'active' ? 'reactivated' : 'suspended'} successfully`);
        onUserUpdated?.();
      } else {
        toast.error(data.message || 'Failed to update user status');
      }
    } catch (err) {
      console.error('Error updating user status:', err);
      toast.error('Server error while updating user status');
    } finally {
      setStatusChanging(false);
    }
  };

  const deleteUser = async () => {
    const confirmed = window.confirm('⚠️ This action cannot be undone. Delete this user permanently?');
    if (!confirmed) return;

    setDeleting(true);
    try {
      const response = await fetch(`http://localhost:5003/admin/users/${user._id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success('User deleted');
        onUserUpdated?.();
        onClose();
      } else {
        toast.error('Failed to delete user');
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      toast.error('Server error while deleting user');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex">
      <Toaster position="top-right" />
      <div className="fixed inset-0 bg-black bg-opacity-30" onClick={onClose}></div>

      <div className="ml-auto w-full max-w-md bg-white shadow-xl p-6 z-50 overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">User Details</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-red-500 text-lg font-bold"
          >
            ×
          </button>
        </div>

        <div className="space-y-4 text-sm">
        <div><span className="font-medium text-gray-600 font-semibold">Status:</span> <span
    className={`px-2 py-1 rounded-full text-xs font-medium ${
      user.status === 'active'
        ? 'bg-green-100 text-green-700'
        : user.status === 'suspended'
        ? 'bg-yellow-100 text-yellow-800'
        : 'bg-gray-100 text-gray-600'
    }`}
  >
    {user.status}
  </span></div>
  <div><span className="font-medium text-gray-600 font-semibold">Created On:</span> {new Date(user.createdAt).toLocaleString()}</div>
          <div><span className="font-medium text-gray-600 font-semibold">Name:</span> {user.name || '-'}</div>
          <div><span className="font-medium text-gray-600 font-semibold">Email:</span> {user.email || '-'}</div>
          <div><span className="font-medium text-gray-600 font-semibold">Plan:</span> {user.plan || '-'}</div>
          <div><span className="font-medium text-gray-600 font-semibold">Country:</span> {user.country || '-'}</div>
          
          <div><span className="font-medium text-gray-600 font-semibold">Company Name:</span> {user.company || '-'}</div>
          <div><span className="font-medium text-gray-600 font-semibold">Address:</span> {user.address || '-'}</div>
          <div><span className="font-medium text-gray-600 font-semibold">Phone:</span> {user.phone || '-'}</div>
          <div><span className="font-medium text-gray-600 font-semibold">Role:</span> {user.role || '-'}</div>
          <div><span className="font-medium text-gray-600 font-semibold">Source:</span> {user.source || '-'}</div>
        </div>

        {/* Plan Change Section */}
        <div className="mt-3">
          <label className="block text-sm font-medium text-gray-600 mb-1 font-semibold">Change Plan</label>
          <div className="flex gap-2 items-center">
            <select
              className="border rounded-md px-3 py-2 text-sm w-full"
              value={selectedPlan}
              onChange={(e) => setSelectedPlan(e.target.value)}
              disabled={updatingPlan}
            >
              <option value="free">Free</option>
              <option value="pro">Pro</option>
              <option value="premium">Premium</option>
            </select>
            <button
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
              onClick={updatePlan}
              disabled={updatingPlan || selectedPlan === user.plan}
            >
              Change
            </button>
          </div>
          {updatingPlan && <p className="text-xs text-blue-600 mt-1">Updating plan...</p>}
        </div>

        {/* Suspend / Reactivate Button */}
        <div className="mt-6 flex gap-3">
          {user.status === 'suspended' ? (
            <button
              onClick={() => changeUserStatus('active')}
              disabled={statusChanging}
              className="px-4 py-2 text-sm bg-green-100 hover:bg-green-200 rounded-md text-green-700"
            >
              {statusChanging ? 'Reactivating...' : 'Reactivate User'}
            </button>
          ) : (
            <button
              onClick={() => changeUserStatus('suspended')}
              disabled={statusChanging}
              className="px-4 py-2 text-sm bg-red-100 hover:bg-red-200 rounded-md text-red-600"
            >
              {statusChanging ? 'Suspending...' : 'Suspend User'}
            </button>
            
          )}
          <button
            onClick={deleteUser}
            disabled={deleting}
            className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            {deleting ? 'Deleting...' : 'Delete User'}
          </button>
        </div>

        

        {/* Activity Log Placeholder */}
        <div className="mt-8">
          <h4 className="text-sm font-semibold mb-2">Activity Log</h4>
          <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
            {activityLogs.length > 0 ? (
              activityLogs.map((log) => (
                <div key={log._id} className="text-sm text-gray-700 border-b pb-1">
                  <div>{log.action.replace('_', ' ')}: {log.details}</div>
                  <div className="text-xs text-gray-400">{new Date(log.timestamp).toLocaleString()}</div>
                </div>
              ))
            ) : (
              <div className="text-sm text-gray-500">No logs found.</div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            className="px-4 py-2 text-sm bg-gray-200 rounded-md hover:bg-gray-300"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserDetailsDrawer;
